import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { COMMAND_PAYLOAD_FIELDS } from './model';

/**
 * E34 · the practice's homework commands from the portal — AssignHomework, AmendHomework,
 * CancelHomework, ReopenHomework, RequestHomeworkReminder, and (block 4) CommentOnHomework — each ONE deliberate act (a button),
 * posted with `errorNotification: false` so the screen reports the outcome itself (portal
 * conventions): a CODED refusal (`homework.errors.*`, already in the therapist's language) is shown
 * verbatim, against the thing it refused; anything else is the screen's own generic copy plus
 * Sentry. Nothing here retries or autosaves.
 */
export type HomeworkOperation =
  | 'assignHomework'
  | 'amendHomework'
  | 'cancelHomework'
  | 'reopenHomework'
  | 'requestHomeworkReminder'
  | 'commentOnHomework';

const INPUT_TYPE: Record<HomeworkOperation, string> = {
  assignHomework: 'AssignHomeworkInput!',
  amendHomework: 'AmendHomeworkInput!',
  cancelHomework: 'CancelHomeworkInput!',
  reopenHomework: 'ReopenHomeworkInput!',
  requestHomeworkReminder: 'RequestHomeworkReminderInput!',
  commentOnHomework: 'CommentOnHomeworkInput!',
};

export type HomeworkCommandResult =
  | { ok: true; assignmentId: string; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useHomeworkCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    assignmentId: string;
    recorded: boolean;
  }>();

  const run = useCallback(
    async (
      operation: HomeworkOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<HomeworkCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: COMMAND_PAYLOAD_FIELDS,
            variables: {
              input: {
                name: 'input',
                type: INPUT_TYPE[operation],
                value: input,
              },
            },
          },
        });
        const data = res?.data;
        if (!data?.assignmentId) {
          // a null answer is a refusal, never a save (portal conventions)
          return { ok: false, code: undefined, message: fallback };
        }
        return {
          ok: true,
          assignmentId: data.assignmentId,
          recorded: !!data.recorded,
        };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
